import { createElement } from "react";

import {
  CreditCardIcon,
  DollarSignIcon,
  LaptopMinimalIcon,
  PackageIcon,
  PackageOpenIcon,
  SmartphoneIcon,
  TruckIcon,
  UsersRoundIcon,
  WalletIcon,
  ShoppingBagIcon,
  HeadphonesIcon,
  WatchIcon,
} from "lucide-react";

import type { AdvertisementCardProps } from "./widget-advertisement";
import type { ForBusinessSharkCardProps } from "./widget-for-business-shark";
import type { MonthlyCampaignCardProps } from "./widget-monthly-campaign";
import type { OrdersCardProps } from "./widget-orders";
import type { PaymentHistoryCardProps } from "./widget-payment-history";
import type { PopularProductCardProps } from "./widget-popular-product";
import type { ProductInsightsCardProps } from "./widget-product-insights";
import type { SalesByCountryCardProps } from "./widget-sales-by-countries";
import type { TopProductsCardProps } from "./widget-top-products";
import type { TotalEarningWidgetCardProps } from "./widget-total-earning";
import type { TransactionsCardProps } from "./widget-transactions";
import type { UpgradeYourPlanCardProps } from "./widget-upgrade-your-plan";
import type { UserOrderCardProps } from "./widget-user-order";
import type { VehiclesConditionCardProps } from "./widget-vehicles-condition";

export const demoWidgetMenuItems = ["Share", "Update", "Refresh"];

export const advertisementDemoProps: AdvertisementCardProps = {
  title: "Design strategy master class",
  dateTime: "07 Jun 2025 at 10:00 PM",
  day: "12",
  month: "Dec",
  description:
    "How to improve your next design's strategy that works for user and business",
  tags: ["Technical", "User research", "Analytics"],
  host: { name: "Hallie Richards", fallback: "HR" },
  avatars: [
    { fallback: "OS", name: "Olivia Sparks" },
    { fallback: "HL", name: "Howard Lloyd" },
    { fallback: "HR", name: "Hallie Richards" },
    { fallback: "JW", name: "Jenny Wilson" },
  ],
  joinLabel: "Join now",
  likesCount: "56k",
  commentsCount: "2k",
  menuItems: demoWidgetMenuItems,
};

export const forBusinessSharkDemoProps: ForBusinessSharkCardProps = {
  title: "For Business Shark",
  description:
    "Here, I focus on a range of items and features that we use in life without them",
  choosePlanLabel: "Choose a plan to get started",
  plans: [
    { name: "Branding", price: 60, checked: false },
    { name: "Marketing", price: 120, checked: true },
    { name: "Web Development", price: 250, checked: false },
    { name: "App Development", price: 320, checked: false },
  ],
  taxesLabel: "Taxes",
  taxesAmount: "$32",
  totalLabel: "Total amount",
  totalAmount: "$152",
  payLabel: "Pay now",
  menuItems: demoWidgetMenuItems,
};

export const monthlyCampaignDemoProps: MonthlyCampaignCardProps = {
  title: "Monthly Campaign",
  subTitle: "Campaign performance overview",
  campaignData: [
    {
      icon: createElement(UsersRoundIcon),
      title: "New visitors",
      value: "8,549",
      percentage: "+12.5%",
      avatarClassName: "bg-success/10 text-success",
    },
    {
      icon: createElement(ShoppingBagIcon),
      title: "Total orders",
      value: "1,423",
      percentage: "+8.2%",
      avatarClassName: "bg-info/10 text-info",
    },
    {
      icon: createElement(DollarSignIcon),
      title: "Revenue",
      value: "$9,874",
      percentage: "-2.4%",
      avatarClassName: "bg-warning/10 text-warning",
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const ordersDemoProps: OrdersCardProps = {
  title: "Orders",
  subTitle: "75 Deliveries in progress",
  senderLabel: "Sender",
  receiverLabel: "Receiver",
  tabs: [
    {
      name: "New",
      value: "new",
      content: [
        {
          sender: "Mytrle Ullrich",
          senderContent: "101 Boulder, California(CA), 959595",
          receiver: "Barry Schowalter",
          receiverContent: "939 orange, California(CA), 92118",
        },
        {
          sender: "Lucas Smith",
          senderContent: "203 Riverdale, New York(NY), 10001",
          receiver: "Emma Johnson",
          receiverContent: "305 Maple Avenue, Austin, Texas(TX), 73301",
        },
      ],
    },
    {
      name: "Pending",
      value: "pending",
      content: [
        {
          sender: "Ava Wilson",
          senderContent: "Your package has been dispatched",
          receiver: "Ryan Taylor",
          receiverContent: "The package is out for delivery today",
        },
        {
          sender: "Olivia Brown",
          senderContent: "Your package has been dispatched",
          receiver: "James Davis",
          receiverContent:
            "The package was successfully delivered today at 12:30 PM",
        },
      ],
    },
    {
      name: "Shipping",
      value: "shipping",
      content: [
        {
          sender: "Noah Parker",
          senderContent: "Delivering in 2 days from now (July 13, 2025)",
          receiver: "Grace Kim",
          receiverContent: "939 orange, California(CA), 92118",
        },
        {
          sender: "Lily Wang",
          senderContent: "July 11, 2025 (Delivered at 12:30 PM)",
          receiver: "Maya Singh",
          receiverContent: "July 11, 2025 (Delivered at 12:30 PM)",
        },
      ],
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const paymentHistoryDemoProps: PaymentHistoryCardProps = {
  title: "Payment history",
  headers: { card: "Card", date: "Date", spend: "Spend" },
  paymentData: [
    {
      cardNumber: "4123",
      cardType: "Mastercard",
      date: "12 Dec 2025",
      spend: "$120.00",
      remaining: "$4,500",
    },
    {
      cardNumber: "8562",
      cardType: "Visa",
      date: "10 Dec 2025",
      spend: "$89.50",
      remaining: "$2,140",
    },
    {
      cardNumber: "7201",
      cardType: "Amex",
      date: "02 Dec 2025",
      spend: "$240.99",
      remaining: "$8,320",
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const popularProductDemoProps: PopularProductCardProps = {
  title: "Popular Products",
  subTitle: "Total 10.4k visitors",
  productsData: [
    { productName: "Apple iPhone 13", price: "$999", visits: "12.4k" },
    { productName: "Nike Air Jordan", price: "$249", visits: "9.2k" },
    { productName: "Oneplus Nord", price: "$499", visits: "6.8k" },
    { productName: "Sony Headphones", price: "$199", visits: "4.1k" },
  ],
  menuItems: demoWidgetMenuItems,
};

const insightsChartData = [
  { month: "January", value: 168 },
  { month: "February", value: 305 },
  { month: "March", value: 213 },
  { month: "April", value: 330 },
  { month: "May", value: 305 },
];

export const productInsightsDemoProps: ProductInsightsCardProps = {
  title: "Product insight",
  subTitle: "Published on 12 MAY 2025 - 6:10 PM",
  reachedLabel: "Product reached",
  reachedValue: "21,153",
  reachedChartData: insightsChartData,
  ordersLabel: "Order placed",
  ordersValue: "2,123",
  ordersChartData: insightsChartData,
  reachedSeriesLabel: "Reached",
  ordersSeriesLabel: "Orders",
};

export const salesByCountriesDemoProps: SalesByCountryCardProps = {
  title: "Sales by Countries",
  subTitle: "Monthly sales overview",
  salesData: [
    { sales: "$8,656k", country: "USA", changePercentage: "25.8%", trend: "up" },
    { sales: "$2,415k", country: "Brazil", changePercentage: "6.2%", trend: "down" },
    { sales: "$865k", country: "India", changePercentage: "12.4%", trend: "up" },
    { sales: "$745k", country: "Australia", changePercentage: "11.9%", trend: "down" },
    { sales: "$45k", country: "France", changePercentage: "16.2%", trend: "up" },
  ],
  menuItems: demoWidgetMenuItems,
};

export const topProductsDemoProps: TopProductsCardProps = {
  salesTitle: "Top Products by Sales",
  volumeTitle: "Top Products by Volume",
  productsBySalesData: [
    {
      icon: createElement(SmartphoneIcon),
      productName: "Oneplus Nord",
      productBrand: "Oneplus",
      sales: "12.4k",
      iconClassName: "bg-info/10 text-info",
    },
    {
      icon: createElement(LaptopMinimalIcon),
      productName: "MacBook Air",
      productBrand: "Apple",
      sales: "9.2k",
      iconClassName: "bg-success/10 text-success",
    },
    {
      icon: createElement(HeadphonesIcon),
      productName: "Sony WH-1000XM5",
      productBrand: "Sony",
      sales: "6.8k",
      iconClassName: "bg-warning/10 text-warning",
    },
  ],
  productsByVolumeData: [
    {
      icon: createElement(WatchIcon),
      productName: "Apple Watch SE",
      productBrand: "Apple",
      volume: "8,549",
      changePercentage: 12.5,
      iconClassName: "bg-accent/10 text-accent-dark",
    },
    {
      icon: createElement(SmartphoneIcon),
      productName: "Galaxy S23",
      productBrand: "Samsung",
      volume: "7,213",
      changePercentage: -3.2,
      iconClassName: "bg-brand-500/10 text-brand-500",
    },
    {
      icon: createElement(LaptopMinimalIcon),
      productName: "ThinkPad X1",
      productBrand: "Lenovo",
      volume: "5,120",
      changePercentage: 8.4,
      iconClassName: "bg-info/10 text-info",
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const totalEarningWidgetDemoProps: TotalEarningWidgetCardProps = {
  title: "Total Earning",
  earning: "$24,895",
  trend: "up",
  percentage: 10,
  comparisonText: "Compared to $84,325 last year",
  earningData: [
    {
      platform: "Zipcar",
      technologies: "Vuejs, React & HTML",
      earnings: "$24,895.65",
      progressPercentage: 75,
    },
    {
      platform: "Bitbank",
      technologies: "Sketch, Figma & XD",
      earnings: "$8,650.20",
      progressPercentage: 45,
    },
    {
      platform: "Aviato",
      technologies: "HTML & Angular",
      earnings: "$1,245.80",
      progressPercentage: 25,
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const transactionsDemoProps: TransactionsCardProps = {
  title: "Transactions",
  transactions: [
    {
      icon: createElement(WalletIcon),
      paymentMethod: "Wallet",
      platform: "Starbucks",
      amount: "$75",
      paymentType: "debit",
      iconClassName: "bg-accent/10 text-accent-dark",
    },
    {
      icon: createElement(CreditCardIcon),
      paymentMethod: "Mastercard",
      platform: "Refund",
      amount: "$80",
      paymentType: "credit",
      iconClassName: "bg-success/10 text-success",
    },
    {
      icon: createElement(DollarSignIcon),
      paymentMethod: "Bank Transfer",
      platform: "Salary",
      amount: "$2,150",
      paymentType: "credit",
      iconClassName: "bg-info/10 text-info",
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const upgradeYourPlanDemoProps: UpgradeYourPlanCardProps = {
  title: "Upgrade your plan",
  description:
    "To fully enjoy all the amazing features and benefits of our premium plan.",
  plan: {
    name: "Platinum",
    period: "Last 6 months",
    price: "$5,550",
    periodPriceLabel: "/Year",
  },
  paymentDetailsLabel: "Payment details",
  paymentMethods: [
    {
      id: "card-1",
      label: "Credit card",
      maskedNumber: "5688 xxxx xxxx 2356",
    },
    {
      id: "card-2",
      label: "Credit card",
      maskedNumber: "8562 xxxx xxxx 4563",
    },
  ],
  cvcPlaceholder: "CVC",
  addPaymentLabel: "Add Payment method",
  cardNumberPlaceholder: "Card Number",
  payLabel: "Pay now",
  menuItems: demoWidgetMenuItems,
};

export const userOrderDemoProps: UserOrderCardProps = {
  user: { handle: "@jackwilliams", role: "Business", fallback: "JW" },
  ordersCount: "4,689",
  ordersLabel: "Orders",
  tabs: [
    {
      name: "Packed",
      value: "packed",
      icon: createElement(PackageIcon),
      contentData: [
        { label: "Packing Pending", value: 4250, progress: 80 },
        { label: "Packing in Progress", value: 2150, progress: 60 },
        { label: "Packing Complete", value: 1750, progress: 40 },
      ],
    },
    {
      name: "Shipped",
      value: "shipped",
      icon: createElement(TruckIcon),
      contentData: [
        { label: "Shipping Pending", value: 3250, progress: 70 },
        { label: "Shipping in Progress", value: 1150, progress: 50 },
        { label: "Shipping Complete", value: 950, progress: 30 },
      ],
    },
    {
      name: "Received",
      value: "received",
      icon: createElement(PackageOpenIcon),
      contentData: [
        { label: "Receiving Pending", value: 2250, progress: 80 },
        { label: "Receiving in Progress", value: 1150, progress: 50 },
        { label: "Receiving Complete", value: 950, progress: 30 },
      ],
    },
  ],
  menuItems: demoWidgetMenuItems,
};

export const vehiclesConditionDemoProps: VehiclesConditionCardProps = {
  title: "Vehicles Condition",
  vehicleConditionData: [
    {
      condition: "Incorrect address",
      details: "12 vehicles",
      progressValue: 42,
      changePercentage: "+6.1%",
      progressClassName: "stroke-destructive",
    },
    {
      condition: "Good conditions",
      details: "24 vehicles",
      progressValue: 78,
      changePercentage: "+8.7%",
      progressClassName: "stroke-success",
    },
    {
      condition: "Needs maintenance",
      details: "6 vehicles",
      progressValue: 20,
      changePercentage: "-2.3%",
      progressClassName: "stroke-warning",
    },
  ],
  menuItems: demoWidgetMenuItems,
};
